import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { Highlight } from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { TextAlign } from '@tiptap/extension-text-align';
import type { Editor } from '@tiptap/core';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Highlighter,
  List,
  ListOrdered,
  ListTodo,
  Link as LinkIcon,
  RemoveFormatting,
  Table as TableIcon,
  TableCellsMerge,
  Plus,
  Minus,
  Undo2,
  Redo2,
  Scissors,
  Copy,
  ClipboardPaste,
} from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { CloseButton } from '@/components/ui/CloseButton';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { BodyText } from '@/components/ui/BodyText';
import { usePatientNotesStore } from '@/stores/patientNotes/usePatientNotesStore';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';
import { markdownToHtml } from './PatientNoteListItemContentCard';

// Convert Tiptap HTML output to Markdown
function htmlToMarkdown(html: string): string {
  let md = html;

  // Normalize text alignment into custom fences so we can round-trip alignment.
  md = md.replace(
    /<(p|h1|h2|h3|h4)[^>]*(?:style="[^"]*text-align:\s*(center|right|justify)[^"]*"|data-text-align="(center|right|justify)")[^>]*>([\s\S]*?)<\/\1>/gi,
    (_match, _tag, styleAlign, dataAlign, content) => {
      const align = (styleAlign || dataAlign || '').toLowerCase();
      if (!align) return content;
      return `:::align-${align}\n${content}\n:::\n`;
    }
  );

  // Handle headings
  md = md.replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n');
  md = md.replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n');
  md = md.replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n');
  md = md.replace(/<h4[^>]*>(.*?)<\/h4>/gi, '#### $1\n');

  // Handle bold and italic
  md = md.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**');
  md = md.replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**');
  md = md.replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*');
  md = md.replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*');

  // Handle underline (use custom syntax ++text++)
  md = md.replace(/<u[^>]*>(.*?)<\/u>/gi, '++$1++');

  // Handle strikethrough
  md = md.replace(/<s[^>]*>(.*?)<\/s>/gi, '~~$1~~');
  md = md.replace(/<strike[^>]*>(.*?)<\/strike>/gi, '~~$1~~');
  md = md.replace(/<del[^>]*>(.*?)<\/del>/gi, '~~$1~~');

  // Handle highlights (use custom syntax ==text==)
  md = md.replace(/<mark[^>]*>(.*?)<\/mark>/gi, '==$1==');

  // Handle links
  md = md.replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)');

  // Strip blockquotes to plain text (no special handling)
  md = md.replace(/<blockquote[^>]*>(.*?)<\/blockquote>/gis, (_, content) => {
    return content.replace(/<\/?p[^>]*>/gi, '').trim() + '\n';
  });

  // Handle unordered lists
  md = md.replace(/<ul[^>]*>(.*?)<\/ul>/gis, (_, content) => {
    const items = content.match(/<li[^>]*>(.*?)<\/li>/gis) || [];
    return items.map((item: string) => {
      const text = item.replace(/<\/?li[^>]*>/gi, '').replace(/<\/?p[^>]*>/gi, '').trim();
      return `- ${text}`;
    }).join('\n') + '\n';
  });

  // Handle ordered lists
  md = md.replace(/<ol[^>]*>(.*?)<\/ol>/gis, (_, content) => {
    const items = content.match(/<li[^>]*>(.*?)<\/li>/gis) || [];
    return items.map((item: string, index: number) => {
      const text = item.replace(/<\/?li[^>]*>/gi, '').replace(/<\/?p[^>]*>/gi, '').trim();
      return `${index + 1}. ${text}`;
    }).join('\n') + '\n';
  });

  // Handle task lists
  md = md.replace(/<ul[^>]*data-type="taskList"[^>]*>(.*?)<\/ul>/gis, (_, content) => {
    const items = content.match(/<li[^>]*>(.*?)<\/li>/gis) || [];
    return items.map((item: string) => {
      const isChecked = /data-checked="true"/.test(item);
      const text = item.replace(/<\/?li[^>]*>/gi, '').replace(/<\/?p[^>]*>/gi, '')
        .replace(/<label[^>]*>.*?<\/label>/gi, '').replace(/<\/?div[^>]*>/gi, '').trim();
      return isChecked ? `- [x] ${text}` : `- [ ] ${text}`;
    }).join('\n') + '\n';
  });

  // Handle paragraphs
  md = md.replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n');

  // Handle line breaks
  md = md.replace(/<br\s*\/?>/gi, '\n');

  // Remove any remaining HTML tags (tables, etc. - keep as HTML for complex structures)
  // For tables, we'll keep them as-is since markdown tables are limited

  // Clean up extra whitespace
  md = md.replace(/\n{3,}/g, '\n\n');
  md = md.trim();

  return md;
}

// Tiptap editor styles for proper rendering of rich text elements
const getEditorStyles = (isDark: boolean) => `
  .ProseMirror {
    outline: none;
    min-height: 180px;
  }
  .ProseMirror h1 {
    font-size: 2rem;
    font-weight: 700;
    margin-top: 1.25rem;
    margin-bottom: 0.75rem;
  }
  .ProseMirror h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }
  .ProseMirror h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 0.75rem;
    margin-bottom: 0.5rem;
  }
  .ProseMirror h4 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-top: 0.5rem;
    margin-bottom: 0.25rem;
  }
  .ProseMirror ul {
    list-style-type: disc;
    padding-left: 1.5rem;
    margin: 0.5rem 0;
  }
  .ProseMirror ol {
    list-style-type: decimal;
    padding-left: 1.5rem;
    margin: 0.5rem 0;
  }
  .ProseMirror li {
    margin: 0.25rem 0;
  }
  .ProseMirror li p {
    margin: 0;
  }
  .ProseMirror ul[data-type="taskList"] {
    list-style: none;
    padding-left: 0;
  }
  .ProseMirror ul[data-type="taskList"] li {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
  }
  .ProseMirror ul[data-type="taskList"] li > label {
    flex-shrink: 0;
    margin-top: 0.25rem;
  }
  .ProseMirror ul[data-type="taskList"] li > label input[type="checkbox"] {
    width: 1rem;
    height: 1rem;
    cursor: pointer;
    accent-color: ${isDark ? '#60a5fa' : '#2563eb'};
  }
  .ProseMirror ul[data-type="taskList"] li > div {
    flex: 1;
  }
  .ProseMirror ul[data-type="taskList"] li[data-checked="true"] > div {
    text-decoration: line-through;
    color: ${isDark ? '#6b7280' : '#9ca3af'};
  }
  .ProseMirror blockquote {
    border-left: 3px solid ${isDark ? '#6b7280' : '#9ca3af'};
    padding-left: 1rem;
    margin: 0.5rem 0;
    font-style: italic;
    color: ${isDark ? '#9ca3af' : '#6b7280'};
  }
  .ProseMirror a {
    color: ${isDark ? '#60a5fa' : '#2563eb'};
    text-decoration: underline;
    cursor: pointer;
  }
  .ProseMirror a:hover {
    color: ${isDark ? '#93c5fd' : '#1d4ed8'};
  }
  .ProseMirror p {
    margin: 0.25rem 0;
  }
  .ProseMirror strong {
    font-weight: 600;
  }
  .ProseMirror em {
    font-style: italic;
  }
  .ProseMirror u {
    text-decoration: underline;
  }
  .ProseMirror s {
    text-decoration: line-through;
  }
  .ProseMirror mark {
    background-color: ${isDark ? '#854d0e' : '#fef08a'};
    color: ${isDark ? '#fef08a' : '#854d0e'};
    padding: 0.1rem 0.2rem;
    border-radius: 0.125rem;
  }
  .ProseMirror table {
    border-collapse: collapse;
    margin: 0.5rem 0;
    width: 100%;
  }
  .ProseMirror th,
  .ProseMirror td {
    border: 1px solid ${isDark ? '#4b5563' : '#d1d5db'};
    padding: 0.5rem;
    text-align: left;
    vertical-align: top;
  }
  .ProseMirror th {
    background-color: ${isDark ? '#374151' : '#f3f4f6'};
    font-weight: 600;
  }
  .ProseMirror td {
    background-color: ${isDark ? '#1f2937' : '#ffffff'};
  }
  .ProseMirror .selectedCell {
    background-color: ${isDark ? '#1e3a5f' : '#dbeafe'};
  }
`;

interface AddPatientNoteEditorProps {
  dietitianId: string;
  patientId: string;
  /** Optional: supply initial content and callbacks to use this editor for editing existing notes. */
  mode?: 'create' | 'edit';
  initialContent?: string;
  editableUntilLabel?: string;
  isOpenExternal?: boolean;
  onCloseExternal?: () => void;
  onSaveExternal?: (content: string) => Promise<void>;
  triggerLabel?: string;
  hideTrigger?: boolean;
}

export const AddPatientNoteEditor: React.FC<AddPatientNoteEditorProps> = ({
  dietitianId,
  patientId,
  mode = 'create',
  initialContent,
  editableUntilLabel,
  isOpenExternal,
  onCloseExternal,
  onSaveExternal,
  triggerLabel = 'Add Note',
  hideTrigger = false,
}) => {
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const isOpen = isOpenExternal ?? isOpenInternal;
  const [localError, setLocalError] = useState<string | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { isDark } = useTheme();
  const hasInitializedContent = useRef(false);
  const initialContentRef = useRef<string>(initialContent?.trim() ?? '');

  const draft = usePatientNotesStore((s) => s.draft);
  const setDraft = usePatientNotesStore((s) => s.setDraft);
  const addNote = usePatientNotesStore((s) => s.addNote);
  const submitting = usePatientNotesStore((s) => s.submitting);
  const submitError = usePatientNotesStore((s) => s.submitError);

  const toDocContent = (value: string | undefined) => {
    const text = value?.trim() ?? '';
    return {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: text ? [{ type: 'text', text }] : [],
        },
      ],
    };
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        document: false,
        paragraph: false,
        text: false,
        heading: {
          levels: [1, 2, 3, 4],
        },
      }),
      Document,
      Paragraph,
      Text,
      Underline,
      Highlight.configure({
        multicolor: false,
      }),
      Table.configure({
        resizable: false,
      }),
      TableRow,
      TableCell,
      TableHeader,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        autolink: false,
        validate: (href) => /^https?:\/\//.test(href),
      }),
    ],
    content: mode === 'edit' ? markdownToHtml(initialContent ?? '', { escapeHtml: false }) : toDocContent(draft),
    parseOptions: {
      preserveWhitespace: true,
    },
    onUpdate({ editor: updatedEditor }: { editor: Editor }) {
      const currentText = updatedEditor.getText();
      if (mode === 'edit') {
        setHasUnsavedChanges(currentText.trim() !== initialContentRef.current.trim());
      } else {
        setDraft(currentText);
        setHasUnsavedChanges(currentText.trim() !== initialContentRef.current.trim());
      }
    },
    shouldRerenderOnTransaction: false,
  });

  useEffect(() => {
    if (editor && isOpen && !hasInitializedContent.current) {
      const contentToLoad = mode === 'edit' ? initialContent ?? '' : draft;
      const parsedContent =
        mode === 'edit'
          ? markdownToHtml(contentToLoad, { escapeHtml: false })
          : toDocContent(contentToLoad);

      editor.commands.setContent(parsedContent, {
        emitUpdate: false,
        parseOptions: { preserveWhitespace: true },
      });
      // Store initial content when opening
      initialContentRef.current = (contentToLoad ?? '').trim();
      // edit mode relies on initialContentRef diff only
      setHasUnsavedChanges(false);
      hasInitializedContent.current = true;
    }
    if (!isOpen) {
      hasInitializedContent.current = false;
      setHasUnsavedChanges(false);
    }
  }, [editor, isOpen, draft, initialContent, mode]);

  const combinedError = useMemo(() => localError || submitError, [localError, submitError]);

  const formatButtonClass = (isActive: boolean) =>
    cn(
      'p-2 rounded border transition-colors flex items-center justify-center',
      isDark
        ? 'border-secondary-700 text-secondary-100 hover:bg-secondary-800'
        : 'border-secondary-200 text-secondary-800 hover:bg-secondary-50',
      isActive
        ? isDark
          ? 'bg-secondary-800 border-primary-400 text-primary-200'
          : 'bg-secondary-100 border-primary-500 text-primary-700'
        : ''
    );

  const headingLevel = editor?.isActive('heading', { level: 1 })
    ? 1
    : editor?.isActive('heading', { level: 2 })
      ? 2
      : editor?.isActive('heading', { level: 3 })
        ? 3
        : editor?.isActive('heading', { level: 4 })
          ? 4
          : 0;

  const setHeading = (level: number) => {
    if (!editor) return;
    if (level === 0) {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().setHeading({ level: level as 1 | 2 | 3 | 4 }).run();
    }
  };

  const insertTable = () => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const applyLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Enter URL', previousUrl || 'https://');
    if (url === null) return;
    if (!url || !/^https?:\/\//.test(url)) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().setLink({ href: url }).run();
  }, [editor]);

  // Keyboard shortcut handler
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!editor) return;
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod) return;

      const key = e.key.toLowerCase();
      let handled = false;

      switch (key) {
        case 'u':
          e.preventDefault();
          editor.chain().focus().toggleUnderline().run();
          handled = true;
          break;
        case '7':
          if (e.shiftKey) {
            e.preventDefault();
            editor.chain().focus().toggleOrderedList().run();
            handled = true;
          }
          break;
        case '8':
          if (e.shiftKey) {
            e.preventDefault();
            editor.chain().focus().toggleBulletList().run();
            handled = true;
          }
          break;
        case 'q':
          if (e.shiftKey) {
            e.preventDefault();
            editor.chain().focus().toggleBlockquote().run();
            handled = true;
          }
          break;
        case 'k':
          e.preventDefault();
          applyLink();
          handled = true;
          break;
        case '\\':
          e.preventDefault();
          editor.chain().focus().unsetAllMarks().clearNodes().run();
          handled = true;
          break;
      }

      if (handled) {
        e.stopPropagation();
      }
    },
    [editor, applyLink]
  );

  const handleCloseRequest = () => {
    if (hasUnsavedChanges) {
      setShowCloseConfirm(true);
    } else {
      handleCloseConfirmed();
    }
  };

  const handleCloseConfirmed = () => {
    if (onCloseExternal) {
      onCloseExternal();
    } else {
      setIsOpenInternal(false);
    }
    setLocalError(null);
    setShowCloseConfirm(false);
    setHasUnsavedChanges(false);
    initialContentRef.current = '';
  };

  const handleCloseCancelled = () => {
    setShowCloseConfirm(false);
  };

  const handleSave = async () => {
    if (!editor) {
      setLocalError('Editor not initialized.');
      return;
    }

    const plainText = editor.getText();
    if (!plainText || !plainText.trim()) {
      setLocalError('Note cannot be empty.');
      return;
    }

    // Convert HTML to Markdown to preserve formatting
    const htmlContent = editor.getHTML();
    const markdownContent = htmlToMarkdown(htmlContent);

    if (mode === 'edit' && onSaveExternal) {
      await onSaveExternal(markdownContent);
      setLocalError(null);
      setHasUnsavedChanges(false);
      initialContentRef.current = '';
      if (onCloseExternal) {
        onCloseExternal();
      } else {
        setIsOpenInternal(false);
      }
      return;
    }

    if (mode === 'create') {
      const note = await addNote(dietitianId, patientId, markdownContent);
      if (note) {
        setLocalError(null);
        setHasUnsavedChanges(false);
        initialContentRef.current = '';
        setIsOpenInternal(false);
      }
    }
  };

  return (
    <>
      {!hideTrigger && mode === 'create' && (
        <Button variant="primary" size="sm" onClick={() => setIsOpenInternal(true)} data-testid="open-add-note">
          {triggerLabel}
        </Button>
      )}

      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <Card
            className={cn(
              'w-full max-w-3xl p-6 space-y-4 transition-colors duration-200',
            )}
          >
            <div className="flex items-center justify-end">
              <CloseButton onClick={handleCloseRequest} ariaLabel="Close editor" />
            </div>

            <div className="flex gap-1 flex-wrap items-center">
              <button
                type="button"
                title="Undo (Ctrl/Cmd+Z)"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor?.chain().focus().undo().run();
                }}
                className={formatButtonClass(false)}
                aria-label="Undo"
                disabled={!editor?.can().undo()}
              >
                <Undo2 size={16} aria-hidden />
              </button>
              <button
                type="button"
                title="Redo (Ctrl/Cmd+Y)"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor?.chain().focus().redo().run();
                }}
                className={formatButtonClass(false)}
                aria-label="Redo"
                disabled={!editor?.can().redo()}
              >
                <Redo2 size={16} aria-hidden />
              </button>

              <div className={cn('w-px h-6 mx-1', isDark ? 'bg-secondary-700' : 'bg-secondary-200')} />

              <button
                type="button"
                title="Cut (Ctrl/Cmd+X)"
                onMouseDown={async (e) => {
                  e.preventDefault();
                  const { state } = editor!.view;
                  const { from, to } = state.selection;
                  const text = state.doc.textBetween(from, to, ' ');
                  if (text) {
                    await navigator.clipboard.writeText(text);
                    editor?.chain().focus().deleteSelection().run();
                  }
                }}
                className={formatButtonClass(false)}
                aria-label="Cut"
              >
                <Scissors size={16} aria-hidden />
              </button>
              <button
                type="button"
                title="Copy (Ctrl/Cmd+C)"
                onMouseDown={async (e) => {
                  e.preventDefault();
                  const { state } = editor!.view;
                  const { from, to } = state.selection;
                  const text = state.doc.textBetween(from, to, ' ');
                  if (text) {
                    await navigator.clipboard.writeText(text);
                  }
                }}
                className={formatButtonClass(false)}
                aria-label="Copy"
              >
                <Copy size={16} aria-hidden />
              </button>
              <button
                type="button"
                title="Paste (Ctrl/Cmd+V)"
                onMouseDown={async (e) => {
                  e.preventDefault();
                  const text = await navigator.clipboard.readText();
                  if (text) {
                    editor?.chain().focus().insertContent(text).run();
                  }
                }}
                className={formatButtonClass(false)}
                aria-label="Paste"
              >
                <ClipboardPaste size={16} aria-hidden />
              </button>

              <div className={cn('w-px h-6 mx-1', isDark ? 'bg-secondary-700' : 'bg-secondary-200')} />
              <button
                type="button"
                title="Bold (Ctrl/Cmd+B)"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor?.chain().focus().toggleBold().run();
                }}
                className={formatButtonClass(editor?.isActive('bold') ?? false)}
                aria-label="Toggle bold"
              >
                <Bold size={16} aria-hidden />
              </button>
              <button
                type="button"
                title="Italic (Ctrl/Cmd+I)"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor?.chain().focus().toggleItalic().run();
                }}
                className={formatButtonClass(editor?.isActive('italic') ?? false)}
                aria-label="Toggle italics"
              >
                <Italic size={16} aria-hidden />
              </button>
              <button
                type="button"
                title="Underline (Ctrl/Cmd+U)"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor?.chain().focus().toggleUnderline().run();
                }}
                className={formatButtonClass(editor?.isActive('underline') ?? false)}
                aria-label="Toggle underline"
              >
                <UnderlineIcon size={16} aria-hidden />
              </button>
              <button
                type="button"
                title="Strikethrough"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor?.chain().focus().toggleStrike().run();
                }}
                className={formatButtonClass(editor?.isActive('strike') ?? false)}
                aria-label="Toggle strikethrough"
              >
                <Strikethrough size={16} aria-hidden />
              </button>
              <button
                type="button"
                title="Highlight"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor?.chain().focus().toggleHighlight().run();
                }}
                className={formatButtonClass(editor?.isActive('highlight') ?? false)}
                aria-label="Toggle highlight"
              >
                <Highlighter size={16} aria-hidden />
              </button>

              <div className={cn('w-px h-6 mx-1', isDark ? 'bg-secondary-700' : 'bg-secondary-200')} />

              <select
                aria-label="Text style"
                value={headingLevel}
                onChange={(e) => setHeading(Number(e.target.value))}
                className={cn(
                  'px-2 py-1.5 text-sm rounded border h-8',
                  isDark ? 'bg-secondary-900 border-secondary-700 text-secondary-100' : 'bg-white border-secondary-200 text-secondary-800'
                )}
                title="Text style"
              >
                <option value={0}>Normal</option>
                <option value={1}>Heading 1</option>
                <option value={2}>Heading 2</option>
                <option value={3}>Heading 3</option>
                <option value={4}>Heading 4</option>
              </select>

              <div className={cn('w-px h-6 mx-1', isDark ? 'bg-secondary-700' : 'bg-secondary-200')} />

              <button
                type="button"
                title="Bullet list (Ctrl/Cmd+Shift+8)"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor?.chain().focus().toggleBulletList().run();
                }}
                className={formatButtonClass(editor?.isActive('bulletList') ?? false)}
                aria-label="Toggle bullet list"
              >
                <List size={16} aria-hidden />
              </button>
              <button
                type="button"
                title="Numbered list (Ctrl/Cmd+Shift+7)"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor?.chain().focus().toggleOrderedList().run();
                }}
                className={formatButtonClass(editor?.isActive('orderedList') ?? false)}
                aria-label="Toggle ordered list"
              >
                <ListOrdered size={16} aria-hidden />
              </button>
              <button
                type="button"
                title="Task list"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor?.chain().focus().toggleTaskList().run();
                }}
                className={formatButtonClass(editor?.isActive('taskList') ?? false)}
                aria-label="Toggle task list"
              >
                <ListTodo size={16} aria-hidden />
              </button>
              <div className={cn('w-px h-6 mx-1', isDark ? 'bg-secondary-700' : 'bg-secondary-200')} />

              <button
                type="button"
                title="Insert table"
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertTable();
                }}
                className={formatButtonClass(editor?.isActive('table') ?? false)}
                aria-label="Insert table"
              >
                <TableIcon size={16} aria-hidden />
              </button>
              {(editor?.isActive('table') || editor?.isActive('tableCell') || editor?.isActive('tableHeader') || editor?.isActive('tableRow')) && (
                <>
                  <button
                    type="button"
                    title="Add column"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor?.chain().focus().addColumnAfter().run();
                    }}
                    className={formatButtonClass(false)}
                    aria-label="Add column"
                  >
                    <TableCellsMerge size={16} aria-hidden />
                    <Plus size={10} className="ml-[-4px] mt-[-8px]" aria-hidden />
                  </button>
                  <button
                    type="button"
                    title="Remove column"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor?.chain().focus().deleteColumn().run();
                    }}
                    className={formatButtonClass(false)}
                    aria-label="Remove column"
                  >
                    <TableCellsMerge size={16} aria-hidden />
                    <Minus size={10} className="ml-[-4px] mt-[-8px]" aria-hidden />
                  </button>
                  <button
                    type="button"
                    title="Add row"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor?.chain().focus().addRowAfter().run();
                    }}
                    className={formatButtonClass(false)}
                    aria-label="Add row"
                  >
                    <span className="text-xs font-medium" aria-hidden>Row</span>
                    <Plus size={10} className="ml-0.5" aria-hidden />
                  </button>
                  <button
                    type="button"
                    title="Remove row"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor?.chain().focus().deleteRow().run();
                    }}
                    className={formatButtonClass(false)}
                    aria-label="Remove row"
                  >
                    <span className="text-xs font-medium" aria-hidden>Row</span>
                    <Minus size={10} className="ml-0.5" aria-hidden />
                  </button>
                  <button
                    type="button"
                    title="Delete table"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor?.chain().focus().deleteTable().run();
                    }}
                    className={cn(formatButtonClass(false), 'text-error-500')}
                    aria-label="Delete table"
                  >
                    <TableIcon size={16} aria-hidden />
                    <Minus size={10} className="ml-[-4px] mt-[-8px]" aria-hidden />
                  </button>
                </>
              )}

              <button
                type="button"
                title="Add or edit link (Ctrl/Cmd+K)"
                onMouseDown={(e) => {
                  e.preventDefault();
                  applyLink();
                }}
                className={formatButtonClass(editor?.isActive('link') ?? false)}
                aria-label="Add link"
              >
                <LinkIcon size={16} aria-hidden />
              </button>
              <button
                type="button"
                title="Clear formatting (Ctrl/Cmd+\)"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor?.chain().focus().unsetAllMarks().clearNodes().run();
                }}
                className={formatButtonClass(false)}
                aria-label="Clear formatting"
              >
                <RemoveFormatting size={16} aria-hidden />
              </button>
            </div>

            <div
              className={cn('rounded border overflow-y-auto max-h-[400px]', isDark ? 'border-secondary-700 bg-secondary-950' : 'border-secondary-200 bg-white')}
              onKeyDown={handleKeyDown}
            >
              <style>{getEditorStyles(isDark)}</style>
              <EditorContent
                editor={editor}
                data-testid="tiptap-editor"
                className={cn('min-h-[200px] p-3 focus:outline-none', isDark ? 'text-secondary-100' : 'text-secondary-900')}
              />
            </div>

            {combinedError && (
              <div
                className={cn(
                  'p-3 rounded',
                  isDark ? 'bg-error-900/25 text-error-200' : 'bg-error-50 text-error-700'
                )}
                role="alert"
              >
                <BodyText>{combinedError}</BodyText>
              </div>
            )}

            {mode === 'edit' && editableUntilLabel && (
              <div
                className={cn(
                  'p-3 rounded border text-sm',
                  isDark ? 'border-secondary-700 text-secondary-200' : 'border-secondary-200 text-secondary-800'
                )}
              >
                Patient note is editable until {editableUntilLabel}
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" onClick={handleCloseRequest} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={submitting}>
                {submitting ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Save Note'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      <ConfirmDialog
        isOpen={showCloseConfirm}
        onClose={handleCloseCancelled}
        onConfirm={handleCloseConfirmed}
        title="Discard unsaved changes?"
        message="You have unsaved changes in your clinical note. Are you sure you want to close without saving?"
        confirmText="Discard"
        cancelText="Keep editing"
        destructive
      />
    </>
  );
};

export default AddPatientNoteEditor;
